import { loadVocabularyCatalog, loadConsolidatedVocabularyTopic } from './vocabularyV2';

// Best-effort word -> translation lookup used by TappableText. Built
// entirely from vocabulary content the app already fetches for its own
// screens (see indexTopicContent, which VocabularyPage could call after
// every topic load, mirroring the mobile app's WordLookupService) — this
// only makes its own network call as a last resort, to seed a starter
// dictionary for lang pairs the user hasn't opened Vocabulary for yet (e.g.
// they go straight to Stories).
//
// Matching is exact-word, case-insensitive — no lemmatization, so inflected
// forms in running text may miss. Acceptable v1 scope (mirrors mobile).

// langPair -> Map<normalized target word, native translation>
const indexes = new Map();
const bootstrapping = new Map();

function langPairKey(nativeCode, targetCode) {
  return `${nativeCode}_${targetCode}`;
}

function norm(word) {
  return String(word || '').toLowerCase().trim();
}

// Merges one topic's already-loaded reader/target sections into the shared
// index. Safe to call repeatedly — later calls just add entries. Sections
// are the { id, items: [{id, text}] } shape loadConsolidatedVocabularyTopic
// returns (pack.reader/pack.target) — items are matched by their stable id,
// same convention as every other native/target pairing in this codebase.
export function indexTopicContent(nativeCode, targetCode, readerSections, targetSections) {
  if (!readerSections || !targetSections) return;
  const key = langPairKey(nativeCode, targetCode);
  let index = indexes.get(key);
  if (!index) {
    index = new Map();
    indexes.set(key, index);
  }
  const readerById = new Map();
  for (const section of readerSections) {
    for (const item of section.items || []) {
      if (item?.text) readerById.set(item.id, item.text);
    }
  }
  for (const section of targetSections) {
    for (const item of section.items || []) {
      const targetWord = item?.text;
      const nativeWord = readerById.get(item?.id);
      if (!targetWord || !nativeWord) continue;
      index.set(norm(targetWord), nativeWord);
    }
  }
}

export async function lookupWord(nativeCode, targetCode, word) {
  const key = langPairKey(nativeCode, targetCode);
  let index = indexes.get(key);
  if (!index || index.size === 0) {
    await bootstrap(nativeCode, targetCode);
    index = indexes.get(key);
  }
  return index?.get(norm(word)) ?? null;
}

function bootstrap(nativeCode, targetCode) {
  const key = langPairKey(nativeCode, targetCode);
  const existing = bootstrapping.get(key);
  if (existing) return existing;

  const promise = (async () => {
    try {
      const catalog = await loadVocabularyCatalog(targetCode);
      const topics = catalog?.thematic || [];
      if (topics.length === 0) return;
      const topicId = topics[0]?.id;
      if (!topicId) return;
      const pack = await loadConsolidatedVocabularyTopic('thematic', targetCode, nativeCode, targetCode, topicId);
      indexTopicContent(nativeCode, targetCode, pack.reader?.sections, pack.target?.sections);
    } catch {
      // Leave the index empty — lookups just report "not found".
    }
  })();
  bootstrapping.set(key, promise);
  return promise.finally(() => bootstrapping.delete(key));
}
